import { parseJson } from 'legacy/lut/classes/helpers/JsonHelper'
import { IFormPartners } from 'legacy/lut/classes/models/IFormPartners'

import { Region } from 'clickNgo/configs/geo'
import { mapPartnerLinksFromCrud } from 'clickNgo/entities/partner/api/mapping'
import { PartnerHierarchyType, PartnerWorkflowStatus } from 'clickNgo/entities/partner/configs'
import { IPartner } from 'clickNgo/entities/partner/models'
import { IForm } from 'core-components/classes/models/IForm'
import { separatePhoneIfFormatted } from 'onboarding/utils/phone'

import { PartnerHelper } from '../../classes/helpers/PartnerHelper'

const getReporterId = (reporterId: unknown): string[] => {
  if (reporterId) {
    if (typeof reporterId === 'string') {
      return reporterId.split(',').map((item) => item.trim())
    } else {
      return reporterId as string[]
    }
  }

  return []
}

const LEGAL_MODIFIED_FIELD_MAPPING = {
  LegalState: 'LegalStateProvince',
}

interface ICheckIsFieldModifiedParams {
  field: string
  entryId: string
  modifiedValues: string[]
  isRuntimeChange?: boolean
}

const checkIsFieldModified = ({
  field,
  entryId,
  modifiedValues,
  isRuntimeChange,
}: ICheckIsFieldModifiedParams) =>
  (isRuntimeChange || entryId) &&
  modifiedValues.includes(LEGAL_MODIFIED_FIELD_MAPPING[field] || field)

const createDefinePartnerField =
  (data: IForm, paramsToCheck: Omit<ICheckIsFieldModifiedParams, 'field'>) => (field: string) =>
    checkIsFieldModified({ field, ...paramsToCheck }) ? data[`${field}Ext`] : data[field]

type TMappedPhoneCodeToCountryCode = { [p: string]: string[] }

interface IDefinePhoneParams {
  data: IFormPartners
  hasChangeRequest: boolean
  isRuntimeChange?: boolean
  isDummyPartner: boolean
  modifiedValues: string[]
  phoneField: string
  phoneCodeField: string
  mappedPhoneCodeToCountryCode: TMappedPhoneCodeToCountryCode
}

/**
 * Although autocorrection gets skipped, phone still must be separated.
 * In case of existing partner, phone gets separated only if it is correctly formatted, otherwise it goes to 'phone' as it is,
 * so user could take actions to save it properly.
 * In case of existing partner having change request, phone gets returned from corresponding *Ext fields, if they are in modifiedValues.
 * In case of dummy partner, phone gets returned from corresponding phone and phoneCode fields.
 */
const definePhoneAndCode = ({
  data,
  hasChangeRequest,
  isRuntimeChange,
  isDummyPartner,
  modifiedValues,
  phoneField,
  phoneCodeField,
  mappedPhoneCodeToCountryCode,
}: IDefinePhoneParams): { phone: string; code: string } => {
  if (isDummyPartner) {
    return {
      phone: data[phoneField] || '',
      code: data[phoneCodeField] || '',
    }
  }

  const isPhoneChanged =
    modifiedValues.includes(phoneField) || modifiedValues.includes(phoneCodeField)
  if ((hasChangeRequest || isRuntimeChange) && isPhoneChanged) {
    const definePartnerField = createDefinePartnerField(data, {
      entryId: data.EntryIdExt,
      modifiedValues,
      isRuntimeChange,
    })

    return {
      phone: definePartnerField(phoneField) || '',
      code: definePartnerField(phoneCodeField) || '',
    }
  }

  return separatePhoneIfFormatted(data[phoneField] || '', mappedPhoneCodeToCountryCode)
}

const defineLegalModifiedValues = (legalModifiedValues: unknown): string[] => {
  if (!legalModifiedValues) {
    return []
  }

  if (typeof legalModifiedValues === 'string') {
    return parseJson<string[]>(legalModifiedValues) || []
  }

  if (Array.isArray(legalModifiedValues)) {
    return legalModifiedValues
  }

  return []
}

/**
 * Skip autocorrection of MainPhone in order to show to user the phone as it is saved in db,
 * to let him take actions to fix it. Without that user sees corrected phone, which in fact can be bounced on server validation,
 * in that case user will be confused, because corrected phone is shown.
 */
export const mapPartnerOrChangeRequestFromCrud = (
  data: IFormPartners,
  mappedPhoneCodeToCountryCode: TMappedPhoneCodeToCountryCode,
  isRuntimeChange?: boolean
): IPartner => {
  const entryId502 = data.LegalEntryIdExt || data.LegalEntryId || 0

  const isNewPartner = !data.PartnerID || PartnerHelper.isDummyPartnerRequest(data.PartnerID)

  const legalModifiedValues = defineLegalModifiedValues(
    data.LegalModifiedValues || data.LegalModifiedValuesExt
  )

  const modifiedValues = data.ModifiedValues ? data.ModifiedValues.split(',') : []

  const { phone, code: phoneCountryCode } = definePhoneAndCode({
    data,
    hasChangeRequest: Boolean(data.EntryIdExt),
    isRuntimeChange,
    isDummyPartner: isNewPartner,
    modifiedValues,
    phoneField: 'MainPhone',
    phoneCodeField: 'MainPhoneCountryCode',
    mappedPhoneCodeToCountryCode,
  })

  const definePartnerField = createDefinePartnerField(data, {
    entryId: data.EntryIdExt,
    modifiedValues,
    isRuntimeChange,
  })
  const definePartnerLegalField = createDefinePartnerField(data, {
    entryId: entryId502,
    modifiedValues: legalModifiedValues,
    isRuntimeChange,
  })

  return {
    altVendorPC: data.AltVendorPC || '',
    accreditations: data.Accreditations || '',
    addressLine1: definePartnerField('AddressLine1') || '',
    addressLine2: definePartnerField('AddressLine2') || '',
    addressLine3: definePartnerField('AddressLine3') || '',
    altVendorPrint: data.AltVendorPrint || '',
    amplifyImpactCountOfStars: data.AmplifyImpactCountOfStars || 0,
    amplifyImpactPledgeType: data.AmplifyImpactPledgeType || 'CATALYST',
    approverComment: data.ProfileApproverComment || '',
    backupContacts: data.BackupContacts ? data.BackupContacts.split(',') : [],
    bankAccount: data.BankAccount || '',
    bankAccountName: data.BankAccountName || '',
    bankName: data.BankName || '',
    city: definePartnerField('City') || '',
    communicationName: definePartnerField('CommunicationName') || '',
    companyDomain: data.CompanyDomain || '',
    companyEnglishName: definePartnerLegalField('CompanyEnglishName') || '',
    companyLegalName: definePartnerLegalField('CompanyLegalName') || '',
    companyLegalNameNonLatin: definePartnerLegalField('CompanyLegalNameNonLatin') || '',
    completeness: data.Completement || 0,
    country: definePartnerField('Country') || '',
    countryCode: data.CountryCode || '',
    coverageNumber: data.CoverageNumber || '',
    coverageUnit: data.CoverageUnit || '',
    description: data.Description || '',
    eCommerce: data.eCommerce || '',
    eCommerceDetails: data.eCommerceDetails || '',
    ecommerceLinks: mapPartnerLinksFromCrud(data.ECommerceUrls || ''),
    email: definePartnerField('Email') || '',
    entryId: data.EntryId || data.EntryID || 0,
    entryId458: data.EntryIdAdd || 0,
    entryId406: data.EntryIdExt || 0,
    facebookLink: data.FacebookLink || '',
    gallery: data.Gallery || '',
    hasNotOnlineCommerce: Boolean(data.ECommerceWeDontSellOnline),
    hasAgreements: Boolean(data.AgreementExisting),
    hasActiveAgreement: Boolean(data.IsAP),
    hierarchyType: (data.Hierarchy || data.HierarchyType || '') as PartnerHierarchyType,
    hours: data.Hours || '',
    hpdPartnerGuid: data.HPD_Partner_GUID || '',
    hpMemberUnderAmplifySince: data.MembershipDate || '',
    hpMemberSince: data.HPMemberSince || '',
    image: data.Image || '',
    isAmplifyImpactAssessmentSigned: Boolean(data.AmplifyImpactAssessmentSigned),
    isAmplifyImpactEligible: Boolean(data.AmplifyImpactIsEligible),
    isAmplifyImpactMinOneDeal: Boolean(data.AmplifyImpactMinOneDeal),
    isAmplifyImpactPledgeSigned: Boolean(data.AmplifyImpactPledgeSigned),
    isAmplifyMember: Boolean(data.IsAmplifyMember),
    isAWDP: Boolean(data.AWDP),
    isQualified: Boolean(data.QualPrintSupplies),
    isBusinessPartner: false,
    isNewPartner,
    isPolyPartner: Boolean(data.IsPolyPartner),
    isTopPartner: Boolean(data.IsTopPartner),
    isDormantDDQNotified: Boolean(data.DormantDDQNotified),
    isr: data.ISR || '',
    lastUpdatedDate: data.LastUpdatedDate || '',
    lat: parseInt(data.Lat || '0', 10),
    legalAddressLine1: definePartnerLegalField('LegalAddr1') || '',
    legalAddressLine2: definePartnerLegalField('LegalAddr2') || '',
    legalAddressLine3: definePartnerLegalField('LegalAddr3') || '',
    legalApproverComment: data.LegalApproverCommentExt || '',
    legalCity: definePartnerLegalField('LegalCity') || '',
    legalCountry: definePartnerLegalField('LegalCountry') || '',
    entryId502,
    legalModified: data.LegalModified || data.LegalModifiedExt || '',
    legalModifiedBy: data.LegalModifiedBy || data.LegalModifiedByExt || '',
    legalModifiedValues,
    legalProfilingComment: data.LegalProfilingComment || '',
    legalRegion: definePartnerLegalField('LegalRegion') || '',
    legalState: definePartnerLegalField('LegalState') || '',
    legalWorkflowStatus: data.LegalWorkflowStatusExt || data.LegalWorkflowStatus || '',
    legalZipCode: definePartnerLegalField('LegalPostalCode') || '',
    link: data.Link || '',
    linkedInLink: data.LinkedInLink || '',
    lng: parseInt(data.Lng || '0', 10),
    locationId: definePartnerField('LocationID') || '',
    loyaltyIndex: data.LoyaltyIndex || 0,
    mainContact: data.MainContact || '',
    market: definePartnerField('Market') || '',
    missedRoles: data.MissedRoles ? data.MissedRoles.split(',') : [],
    modified: data.Modified || '',
    modifiedAdd: data.ModifiedAdd || '',
    modifiedBy: data.ModifiedBy || '',
    modifiedByAdd: data.ModifiedByAdd || '',
    modifiedValues,
    oldPartnerId: '',
    organization: data.Organization || '',
    parentLocationId: data.ParentLocationID || '',
    parentPartnerId: data.ParentPartnerID || '',
    partnerAgrees: data.PartnerAgrees || 0,
    partnerCommunication: data.PartnerCommunication || '',
    partnerId: data.PartnerID || '',
    partnerSince: data.PartnerSince || '',
    partnerType: data.PartnerType || '',
    partnerTypeStartDate: data.PartnerType_StartDate || '',
    phone,
    phoneCountryCode,
    profileTeamComment: data.ProfileTeamComment || '',
    profileUpdateDate: data.Profile_UpdateDate || '',
    programCodes: data.ProgramCodes || [],
    region: (data.Region || '') as Region,
    registrationNumber: data.RegistrationNumber || 0,
    reporterId: getReporterId(data.ReporterID),
    resellerAbInd: data.RESELLER_AB_IND || '',
    responseTimeNumber: data.ResponseTimeNumber || 0,
    responseTimeUnits: data.ResponseTimeUnits || '',
    rplStatus: data.RPL_STATUS || '',
    serviceCoverage: data.ServiceCoverage || 0,
    shouldUpdateInMetalocator: Boolean(data.NeedUpdateInMetalocator),
    socialMediaLinks: mapPartnerLinksFromCrud(definePartnerField('SocialMediaUrls') || ''),
    source: data.Source || '',
    sourceRequester: data.SourceRequestor || '',
    splPriceFlag: data.SPL_PRICE_FLAG || '',
    splPriceOverride: data.SPL_PRICE_OVERIDE || '',
    stage: data.STAGE || '',
    state: definePartnerField('State') || '',
    status: definePartnerField('Status') || '',
    stocking: data.Stocking || '',
    storeNumber: definePartnerField('StoreNumber') || '',
    tagIdList: data.TagIdList ? data.TagIdList.split(',') : [],
    taxId: definePartnerField('TaxID') || '',
    territoryExtensionConducted: data.TerritoryExtensionConducted || 0,
    territoryExtensionsBusinessJustification: data.TerritoryExtensionsBusinessJustification || '',
    territoryExtensionsList: data.TerritoryExtensionsList || '',
    topPartnerId: definePartnerField('TopPartnerID') || '',
    twitterLink: data.TwitterLink || '',
    url: definePartnerField('URL') || '',
    whatsAppLink: data.WhatsAppLink || '',
    workflowStatus: (data.WorkflowStatus || '') as PartnerWorkflowStatus,
    zipCode: definePartnerField('ZIPCode') || '',
    zipCodeCovered: data.ZipCodeCovered || 0,
    zipCodes: data.ZipCodes ? data.ZipCodes.split(',') : [],
    onboardingRequestId: data.OnboardingRequestId || null,
  }
}
